import React, { useCallback, useEffect, useState } from 'react';
import { Title } from '../common/styled';
import { LiveTripsForSchool } from './types';
import liveTrips from '../../apis/liveTrips';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { SnackbarSeverityType, TripStatus } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import LiveTripsTable from './components/LiveTripsTable';
import { transformLiveTripsData } from './utils/transformLiveTripsData';
import { isEmpty } from 'lodash';
import { HeaderWrapper, RefreshButton } from './styled';
import OverlayLoader from '../../components/OverlayLoader/OverlayLoader';
import DialogModal from '../../components/DialogModal';
import { Button, DialogActions, Typography } from '@mui/material';

const LiveTripsDashboardPage = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const [liveTripsData, setLiveTripsData] = useState<LiveTripsForSchool[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<LiveTripsForSchool | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);

  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState<boolean>(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>('');
  const [title, setTitle] = useState<string>('');

  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const fetchLiveTripsData = useCallback(
    async (displayToast = true) => {
      setLiveTripsData([]);
      setLoading(true);
      try {
        const response = await liveTrips.fetchLiveTrips(loggedInUser.school);
        const transformedLiveTripsData = transformLiveTripsData(response.data);
        setLiveTripsData(transformedLiveTripsData);
        showToast(
          'Live trips fetched successfully',
          SnackbarSeverityType.success
        );
        setLoading(false);
      } catch (error: any) {
        if (displayToast) {
          showToast(
            error.errors?.[0].message || 'Error in fetching live trips data',
            SnackbarSeverityType.error
          );
        }
        setLoading(false);
      }
    },
    [loggedInUser.school]
  );

  useEffect(() => {
    fetchLiveTripsData();
  }, [fetchLiveTripsData]);

  const handleEndTripModal = (tripData: LiveTripsForSchool) => {
    setSelectedTrip(tripData);
    setConfirmationMessage('Are you sure you want to end the trip?');
    setTitle('End Trip');
    setConfirmationDialogOpen(true);
  };

  const handleEndTripModalClose = () => {
    setConfirmationDialogOpen(false);
    setConfirmationMessage('');
    setTitle('');
    setSelectedTrip(null);
  };

  const handleEndTripConfirm = async () => {
    if (isEmpty(selectedTrip)) return;
    try {
      await liveTrips.updateLiveTrips(selectedTrip.id, TripStatus.ENDED);
      showToast('Trip ended successfully', SnackbarSeverityType.success);
      handleEndTripModalClose();
      fetchLiveTripsData(false);
    } catch (error: any) {
      showToast(
        error.errors?.[0]?.message || 'Failed to end trip',
        SnackbarSeverityType.error
      );
    }
  };

  const handleSOSAlertModalOpen = (tripData: LiveTripsForSchool) => {
    setSelectedTrip(tripData);
    setConfirmationMessage(
      'Are you sure you to want send an alert to notify all parents for the remaining stops of the current trip for the bus that bus-tracking alerts would be stopped'
    );
    setTitle('Send Alert');
    setConfirmationDialogOpen(true);
  };

  const handleSOSAlertModalClose = () => {
    setConfirmationDialogOpen(false);
    setTitle('');
    setSelectedTrip(null);
  };

  const handleSOSAlertConfirm = async () => {
    if (isEmpty(selectedTrip)) return;
    try {
      const response = await liveTrips.sendLiveTripAlert(selectedTrip?.id);
      const updatedResponse = response as any;
      showToast(updatedResponse?.message, SnackbarSeverityType.success);
      handleSOSAlertModalClose();
    } catch (error: any) {
      showToast(
        error.errors?.[0]?.message || 'Failed to send alert',
        SnackbarSeverityType.error
      );
    }
  };

  return (
    <>
      <HeaderWrapper>
        <Title>Live Trips</Title>
        <RefreshButton onClick={() => fetchLiveTripsData()}>
          Refresh
        </RefreshButton>
      </HeaderWrapper>

      <OverlayLoader show={loading} />

      {!isEmpty(liveTripsData) && (
        <LiveTripsTable
          liveTripsData={liveTripsData}
          endTrip={handleEndTripModal}
          sosAlert={handleSOSAlertModalOpen}
        />
      )}

      {/* <ConfirmationDialog
        open={confirmationDialogOpen}
        message={confirmationMessage}
        onClose={handleEndTripModalClose}
        onConfirm={
          confirmationMessage === 'Are you sure you want to end the trip?'
            ? handleEndTripConfirm
            : handleSOSAlertConfirm
        }
      /> */}

      <DialogModal
        open={confirmationDialogOpen}
        onClose={handleEndTripModalClose}
        title={title}
        maxWidth='sm'
        actions={
          <DialogActions sx={{ p: 0 }}>
            <Button onClick={handleEndTripModalClose} variant='outlined'>
              No, Cancel
            </Button>
            <Button
              onClick={
                confirmationMessage === 'Are you sure you want to end the trip?'
                  ? handleEndTripConfirm
                  : handleSOSAlertConfirm
              }
              variant='contained'
            >
              Yes, Continue
            </Button>
          </DialogActions>
        }
      >
        <Typography variant='body1' sx={{ mb: 2 }}>
          {confirmationMessage}
        </Typography>
      </DialogModal>
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      ></ToastMessage>
    </>
  );
};

export default LiveTripsDashboardPage;
